import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  keyframes,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { useMfa } from "../../contexts/MfaContext";
import { FONT_FAMILY } from "../../common/sharedFunctions";

const shakeKeyframe = keyframes`
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }
`;

export default function FreshMfaModal() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showFreshModal, freshActionDescription, verifyFreshMfa, cancelFreshMfa } = useMfa();

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (showFreshModal) {
      setDigits(["", "", "", "", "", ""]);
      setErrorMsg("");
      setSuccess(false);
      setShake(false);
      setTimeout(() => {
        if (inputRefs.current[0]) inputRefs.current[0].focus();
      }, 200);
    }
  }, [showFreshModal]);

  const handleDigitChange = (index, value) => {
    const clean = value.replace(/\D/g, "");
    if (clean.length > 1) {
      const newDigits = [...digits];
      const startIdx = clean.length >= 6 ? 0 : index;
      for (let i = 0; i < clean.length && startIdx + i < 6; i++) {
        newDigits[startIdx + i] = clean[i];
      }
      setDigits(newDigits);
      setErrorMsg("");
      const nextFocus = Math.min(startIdx + clean.length, 5);
      inputRefs.current[nextFocus]?.focus();
      const full = newDigits.join("");
      if (full.length === 6) {
        handleSubmit(full);
      }
      return;
    }

    const char = clean.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = char || "";
    setDigits(newDigits);
    setErrorMsg("");

    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (char && index === 5) {
      const fullCode = newDigits.join("");
      if (fullCode.length === 6) {
        handleSubmit(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim().replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const newDigits = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);

    if (pasted.length === 6) {
      inputRefs.current[5]?.focus();
      handleSubmit(pasted);
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  const handleSubmit = async (codeStr) => {
    if (loading) return;
    setLoading(true);
    setErrorMsg("");
    setShake(false);

    try {
      await verifyFreshMfa(codeStr);
      setSuccess(true);
    } catch (err) {
      setErrorMsg(err.message || t("mfa_invalid_code", "Code invalide. Veuillez réessayer."));
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e?.preventDefault();
    const fullCode = digits.join("");
    if (fullCode.length < 6) {
      setErrorMsg(t("mfa_enter_code", "Veuillez entrer les 6 chiffres."));
      return;
    }
    handleSubmit(fullCode);
  };

  return (
    <Dialog
      open={showFreshModal}
      onClose={cancelFreshMfa}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          width: "100%",
          maxWidth: { xs: "96%", sm: 520, md: 540 },
          m: { xs: 1, sm: 2 },
          p: { xs: 2.5, sm: 3.5 },
          boxShadow: "0 28px 64px rgba(0,0,0,0.35)",
          animation: shake ? `${shakeKeyframe} 0.5s ease-in-out` : "none",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pt: 1, pb: 1 }}>
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.warning.main, 0.12),
            color: theme.palette.warning.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          {success ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 38, color: theme.palette.success.main }} />
          ) : (
            <LockResetIcon sx={{ fontSize: 38 }} />
          )}
        </Box>
        <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: { xs: "1.3rem", sm: "1.45rem" } }}>
          {t("mfa_fresh_required", "Confirmation de Sécurité")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, fontFamily: FONT_FAMILY, fontSize: { xs: "0.92rem", sm: "0.98rem" } }}>
          {freshActionDescription ||
            t("mfa_fresh_msg", "Cette action sensible nécessite une re-validation immédiate par Google Authenticator.")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "12px", fontFamily: FONT_FAMILY }}>
            {errorMsg}
          </Alert>
        )}

        <Box component="form" onSubmit={handleFormSubmit} sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: { xs: 1.2, sm: 1.8 },
              my: { xs: 2.5, sm: 3 },
            }}
            onPaste={handlePaste}
          >
            {digits.map((digit, idx) => (
              <TextField
                key={idx}
                inputRef={(el) => (inputRefs.current[idx] = el)}
                value={digit}
                onChange={(e) => handleDigitChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                onPaste={handlePaste}
                disabled={loading || success}
                inputProps={{
                  type: "tel",
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  autoComplete: idx === 0 ? "one-time-code" : "off",
                  maxLength: 6,
                  style: {
                    textAlign: "center",
                    fontSize: "1.75rem",
                    fontWeight: 700,
                    fontFamily: "monospace",
                    padding: 0,
                  },
                }}
                sx={{
                  width: { xs: 46, sm: 56 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    height: { xs: 58, sm: 66 },
                  },
                }}
              />
            ))}
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || success}
            sx={{
              mt: 1.5,
              py: 1.5,
              borderRadius: "14px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "1.05rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("mfa_confirm", "Confirmer l'opération")
            )}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
        <Button
          size="small"
          color="inherit"
          onClick={cancelFreshMfa}
          disabled={loading}
          sx={{ textTransform: "none", fontFamily: FONT_FAMILY, color: "text.secondary" }}
        >
          {t("cancel", "Annuler")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
